import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import { QIcon_chevron_down, QIcon_chevron_up } from '@repo/icons/index'
import { useEffect, useState } from 'react'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import LinkLocale from '../../../modules/i18n/LinkLocale'
import { n_sign_up, n_signup, s_black_friday, s_signup } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { CDN_URL, COLORS } from '../../../modules/vars'
import { CheckBoxNew } from '../../_legacy/CheckBoxNew'

interface ConsentCheckBoxesNewProps {
  older14: boolean
  setOlder14: (newValue: boolean) => void
  privacyConsent: boolean
  setPrivacyConsent: (newValue: boolean) => void
  termsConsent: boolean
  setTermsConsent: (newValue: boolean) => void
  mktConsent: boolean
  setMktConsent: (newValue: boolean) => void
  mktEmail: boolean
  setMktEmail: (newValue: boolean) => void
  mktSms: boolean
  setMktSms: (newValue: boolean) => void
  mktPush: boolean
  setMktPush: (newValue: boolean) => void
}

const ConsentCheckBoxesNew = ({
  older14,
  setOlder14,
  privacyConsent,
  setPrivacyConsent,
  termsConsent,
  setTermsConsent,
  mktConsent,
  setMktConsent,
  mktEmail,
  setMktEmail,
  mktSms,
  setMktSms,
  mktPush,
  setMktPush,
}: ConsentCheckBoxesNewProps) => {
  const { t, isLocaleKO } = useTranslation()
  const [showPopup, setShowPopup] = useState(false)
  const isMobile = useIsMobile()
  const [showRequired, setShowRequired] = useState(false)
  const [showOptional, setShowOptioanl] = useState(false)

  // 이벤트 소식 수신 동의 이벤트 시 true
  const mktCheckedAll = mktEmail && mktSms && mktPush
  const requiredCheckedAll = older14 && privacyConsent && termsConsent
  const checkedAll = older14 && privacyConsent && termsConsent && mktCheckedAll

  const setMktAll = (newValue: boolean) => {
    setMktEmail(newValue)
    setMktSms(newValue)
    setMktPush(newValue)
  }
  const setAll = (newValue: boolean) => {
    setOlder14(newValue)
    setPrivacyConsent(newValue)
    setTermsConsent(newValue)
    setMktAll(newValue)
  }

  const handleClickRequired = (newValue) => {
    setOlder14(newValue)
    setPrivacyConsent(newValue)
    setTermsConsent(newValue)
  }

  // mktCheckedAll 과 props의 MktConsent의 sync 맞추기 위함
  useEffect(() => {
    setMktConsent(mktCheckedAll)
  }, [mktCheckedAll])

  return (
    <div className="select-none">
      {/* 전체 동의 */}
      <div onClick={() => setAll(!checkedAll)}>
        <CheckBoxNew
          gap={12}
          mrCheckbox={8}
          checked={checkedAll}
          label={<div className="text-black Title2-18Bd select-none">{t(n_signup.select_all)}</div>}
        />
      </div>

      {/* 14세, 서비스 이용약관, 개인정보 수집 */}
      <div className="flex flex-row flex-nowrap gap-[16px] items-center w-full justify-between">
        <div onClick={() => handleClickRequired(!requiredCheckedAll)}>
          <CheckBoxNew
            mrCheckbox={8}
            checked={older14 && privacyConsent && termsConsent}
            label={
              <div className="text-gray-700 Body1-16Md select-none">{`[${t(s_signup.required)}] ${t(
                n_sign_up.agreement.agree_essential_all
              )}`}</div>
            }
          />
        </div>

        <div onClick={() => setShowRequired(!showRequired)} className="cursor-pointer">
          {showRequired ? <QIcon_chevron_up width={16} height={16} /> : <QIcon_chevron_down width={16} height={16} />}
        </div>
      </div>
      {showRequired && (
        <div className="ml-[16px] flex flex-col gap-y-[16px] mt-[16px]">
          {/* 14세 */}
          <div onClick={() => setOlder14(!older14)}>
            <CheckBoxNew
              mrCheckbox={8}
              checked={older14}
              label={
                <div className="text-gray-700 Body1-16Md select-none">{`[${t(s_signup.required)}] ${t(
                  s_signup.i_older_14
                )}`}</div>
              }
            />
          </div>
          {/* 개인정보 */}
          <div onClick={() => setPrivacyConsent(!privacyConsent)} className="w-full flex flex-row justify-between">
            <CheckBoxNew
              mrCheckbox={8}
              checked={privacyConsent}
              label={
                <div className="text-gray-700 Body1-16Md select-none">
                  <span className="w-full">
                    [{t(s_signup.required)}] {t(s_signup.i_agree_to_privacy)}
                  </span>
                </div>
              }
            />
            <LinkLocale
              target="_blank"
              href={urls.student.policy.privacy}
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <span className="Body2-14Md underline text-gray-500 whitespace-nowrap">{t(n_signup.see_content)}</span>
            </LinkLocale>
          </div>

          {/* 이용약관 */}
          <div onClick={() => setTermsConsent(!termsConsent)} className="w-full flex flex-row justify-between">
            <CheckBoxNew
              mrCheckbox={8}
              checked={termsConsent}
              label={
                <div className="text-gray-700 Body1-16Md">
                  [{t(s_signup.required)}] {t(s_signup.i_agree_to_terms)}{' '}
                </div>
              }
            />
            <LinkLocale
              target="_blank"
              href={urls.student.policy.terms}
              onClick={(e) => {
                e.stopPropagation()
              }}
            >
              <span className="Body2-14Md underline text-gray-500 whitespace-nowrap">{t(n_signup.see_content)}</span>
            </LinkLocale>
          </div>
        </div>
      )}

      {/* 마케팅 */}
      <div className="mt-[16px] flex flex-row w-full justify-between flex-nowrap gap-[16px] items-center">
        <div className="" onClick={() => setMktAll(!mktCheckedAll)}>
          <CheckBoxNew
            mrCheckbox={8}
            checked={mktConsent} // 확실하게 보여주기 위해 mktCheckedAll 대신 mktConsent (약간의 딜레이 존재)
            label={
              <div className="text-gray-700 Body1-16Md">
                [{t(s_signup.optional)}] {t(n_sign_up.agreement.agree_optional_all)}{' '}
              </div>
            }
          />
        </div>

        <div onClick={() => setShowOptioanl(!showOptional)} className="cursor-pointer">
          {showOptional ? <QIcon_chevron_up width={16} height={16} /> : <QIcon_chevron_down width={16} height={16} />}
        </div>
      </div>

      {showOptional && (
        <div className="ml-[16px] flex flex-col gap-y-[16px] mt-[16px]">
          {/* 마케팅 - 이메일 */}
          <div onClick={() => setMktEmail(!mktEmail)}>
            <CheckBoxNew
              mrCheckbox={8}
              checked={mktEmail}
              label={
                <div className="text-gray-700 Body1-16Md">
                  [{t(s_signup.optional)}] {t(s_signup.i_want_to_receive_promotions_list.item1)}{' '}
                </div>
              }
            />
          </div>
          {/* 마케팅 - SMS, 알림톡 */}
          <div onClick={() => setMktSms(!mktSms)}>
            <CheckBoxNew
              mrCheckbox={8}
              checked={mktSms}
              label={
                <div className="text-gray-700 Body1-16Md">
                  [{t(s_signup.optional)}] {t(s_signup.i_want_to_receive_promotions_list.item2)}{' '}
                </div>
              }
            />
          </div>

          {/* 마케팅 - 앱 푸시 */}
          <div onClick={() => setMktPush(!mktPush)}>
            <CheckBoxNew
              mrCheckbox={8}
              checked={mktPush}
              label={
                <div className="text-gray-700 Body1-16Md">
                  [{t(s_signup.optional)}] {t(s_signup.i_want_to_receive_promotions_list.item3)}{' '}
                </div>
              }
            />
          </div>
        </div>
      )}
      <DSModal
        id="TextOnlySMTwoButtonHorizontalPopup"
        isOpen={showPopup}
        setIsOpen={setShowPopup}
        size={isMobile ? 'sm' : 'lg'}
      >
        <DSModal.Title>{t(s_black_friday.marketing_modal.title)}</DSModal.Title>
        <DSModal.Content className="mt-[16px]">
          <img src={`${CDN_URL}/web/promotion/signup-banner-popup-web-${isLocaleKO ? 'ko' : 'en'}.png`} />
        </DSModal.Content>
        <DSModal.ButtonContainer direction="horizontal">
          <DSButton
            size={BUTTON_SIZE.MD}
            type={BUTTON_TYPE.OUTLINE}
            customBgColor={COLORS.WHITE}
            onClick={() => setShowPopup(false)}
          >
            <DSButton.Label>{t(s_black_friday.marketing_modal.btn_disagree)}</DSButton.Label>
          </DSButton>
          <DSButton
            size={BUTTON_SIZE.MD}
            onClick={(e) => {
              setMktAll(true)
              setShowPopup(false)
            }}
          >
            <DSButton.Label>{t(s_black_friday.marketing_modal.btn_agree)}</DSButton.Label>
          </DSButton>
        </DSModal.ButtonContainer>
      </DSModal>
    </div>
  )
}

export default ConsentCheckBoxesNew
